import { saveCategoryValue } from '../../../services/resourceLifecycle';
import React, { useState, useEffect } from 'react';
import { 
  Paper, 
  Typography, 
  Box, 
  IconButton, 
  Button, 
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Collapse,
  Tooltip,
  Chip,
  alpha,
  useTheme,
  Fade
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import AddIcon from '@mui/icons-material/Add';
import TagIcon from '@mui/icons-material/LocalOffer';
import { styled } from '@mui/material/styles';
import { db } from '../../../services/firebase';
import { collection, getCountFromServer } from 'firebase/firestore';
import { useCategoryValues } from '../../../contexts/AttributesContext';
import { formatText } from '../../common/TextFormatterProvider';
import AppDatePicker from '../../common/AppDatePicker';
import {
  buildSearchText,
  canonicalizeTagValue,
  getTypedValueLabel,
  normalizeCategoryType,
  tagValueIdError,
} from '../../../utils/tagValueUtils';
import DeleteResourceDialog from '../../common/DeleteResourceDialog';
import useDeleteConfirmation from '../../common/useDeleteConfirmation';

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  position: 'relative',
  borderRadius: 12,
  backgroundColor: alpha(theme.palette.background.paper, 0.7),
  backdropFilter: 'blur(8px)',
  border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
  transition: 'all 0.3s ease',
  overflow: 'hidden',
  '&:hover': {
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.2)',
    borderColor: alpha(theme.palette.primary.main, 0.15),
  }
}));

const StyledListItem = styled(ListItem)(({ theme }) => ({
  padding: theme.spacing(1.5, 2),
  borderRadius: 8,
  marginBottom: theme.spacing(1),
  transition: 'all 0.2s ease',
  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  backgroundColor: alpha(theme.palette.background.alt || theme.palette.background.paper, 0.3),
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.05),
    transform: 'translateX(2px)',
    boxShadow: '0 2px 6px rgba(0, 0, 0, 0.1)',
  },
}));

const CategoryHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(1, 1, 2, 1),
  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.15)}`,
  marginBottom: theme.spacing(2),
}));

const ValueCount = styled(Chip)(({ theme }) => ({
  backgroundColor: alpha(theme.palette.primary.main, 0.1),
  color: theme.palette.primary.light,
  fontWeight: 500,
  fontSize: '0.75rem',
  height: '24px',
}));

function CategoryCard({ category, onDelete, companyName }) {
  const [expanded, setExpanded] = useState(false);
  // While expanded, this card holds its values open through the AttributesProvider
  // — an add or delete here, or by another user, shows at once. Collapsed, only
  // a count is read (one aggregation read, however many values there are).
  const [valueCount, setValueCount] = useState(null);
  const [newValueDialog, setNewValueDialog] = useState(false);
  const [newValue, setNewValue] = useState('');
  const [error, setError] = useState('');
  const valueDeletion = useDeleteConfirmation({
    resourceType: 'tag_value',
    extraFor: () => ({ category_id: category.id }),
    onPreviewError: (err) => {
      setError(`Failed to check value dependencies: ${err.message}`);
      console.error(err);
    },
    onDeleteError: (err) => {
      setError(`Failed to delete value: ${err.message}`);
      console.error(err);
    },
  });
  const theme = useTheme();
  const categoryType = normalizeCategoryType(category.type);
  const valuesPath = `users/${companyName}/tag_categories/${category.id}/values`;

  const values = useCategoryValues(expanded ? category.id : null)[category.id] ?? null;

  useEffect(() => {
    if (expanded || !companyName) return undefined;
    let cancelled = false;
    getCountFromServer(collection(db, valuesPath))
      .then((snapshot) => { if (!cancelled) setValueCount(snapshot.data().count); })
      .catch((err) => console.error('Error counting category values:', err));
    return () => { cancelled = true; };
  }, [expanded, companyName, valuesPath]);

  const shownCount = values !== null && expanded ? values.length : valueCount;

  const handleAddValue = async () => {
    try {
      const canonicalValue = canonicalizeTagValue(newValue, categoryType);
      if (canonicalValue === null) {
        setError('Value cannot be empty');
        return;
      }

      const displayValue = getTypedValueLabel(canonicalValue, categoryType);
      const idError = tagValueIdError(displayValue);
      if (idError) {
        setError(idError);
        return;
      }
      const valueId = displayValue.trim().replace(/\s+/g, '_');
      
      await saveCategoryValue(companyName, category.id, valueId, {
        name: displayValue,
        value: canonicalValue,
        search_text: buildSearchText(canonicalValue, displayValue),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      });
      
      setNewValueDialog(false);
      setNewValue(categoryType === 'Yes/No' ? '' : '');
      setError('');
    } catch (err) {
      setError(`Failed to add value: ${err.message}`);
      console.error(err);
    }
  };

  const handleOpenDeleteValue = (value) => {
    setError('');
    valueDeletion.open(value);
  };

  const handleDeleteCategory = () => {
    onDelete(category.id);
  };

  const renderValueInput = () => {
    if (categoryType === 'Date') {
      return (
        <AppDatePicker
          label="Value"
          fullWidth
          size="medium"
          value={newValue}
          onChange={(nextDate) => {
            setNewValue(nextDate);
            setError('');
          }}
          slotProps={{
            textField: {
              autoFocus: true,
              margin: 'dense',
              variant: 'outlined',
              error: !!error,
              helperText: error,
              InputProps: { className: 'glow-focus' },
            },
          }}
        />
      );
    }

    if (categoryType === 'Number') {
      return (
        <TextField
          autoFocus
          margin="dense"
          label="Value"
          type="number"
          fullWidth
          variant="outlined"
          value={newValue || ''}
          onChange={(e) => {
            setNewValue(e.target.value);
            setError('');
          }}
          error={!!error}
          helperText={error}
          InputProps={{ className: 'glow-focus' }}
        />
      );
    }

    if (categoryType === 'Yes/No') {
      return (
        <FormControl fullWidth margin="dense" error={!!error}>
          <InputLabel>Value</InputLabel>
          <Select
            autoFocus
            value={newValue === true ? 'true' : newValue === false ? 'false' : ''}
            label="Value"
            onChange={(e) => {
              setNewValue(e.target.value === 'true');
              setError('');
            }}
          >
            <MenuItem value="true">Yes</MenuItem>
            <MenuItem value="false">No</MenuItem>
          </Select>
          {error ? (
            <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
              {error}
            </Typography>
          ) : null}
        </FormControl>
      );
    }

    return (
      <TextField
        autoFocus
        margin="dense"
        label="Value"
        fullWidth
        variant="outlined"
        value={newValue || ''}
        onChange={(e) => {
          setNewValue(e.target.value);
          setError('');
        }}
        error={!!error}
        helperText={error}
        InputProps={{
          className: 'glow-focus',
        }}
      />
    );
  };

  return (
    <StyledPaper elevation={2}>
      <CategoryHeader>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography 
            variant="h6" 
            sx={{ 
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              letterSpacing: '0.01em',
            }}
          >
            {formatText(category.name)}
          </Typography>
          {/* The type is fixed at creation: every table column made from this
              category copied it, and a change here could never reach them (H88).
              A category with the wrong type is deleted and added again. */}
          <ValueCount size="small" label={categoryType} sx={{ ml: 1 }} />
          <ValueCount 
            size="small" 
            label={shownCount === null ? 'values' : `${shownCount} value${shownCount !== 1 ? 's' : ''}`}
            sx={{ ml: 2 }}
          />
        </Box>
        <Box>
          <Tooltip title={expanded ? "Collapse" : "Expand"}>
            <IconButton 
              onClick={() => setExpanded(!expanded)}
              sx={{ 
                color: theme.palette.text.secondary,
                transition: 'transform 0.3s ease',
                transform: expanded ? 'rotate(0deg)' : 'rotate(-90deg)',
                '&:hover': {
                  color: theme.palette.text.primary,
                  backgroundColor: alpha(theme.palette.primary.main, 0.05),
                }
              }}
            >
              {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete Category">
            <IconButton 
              onClick={handleDeleteCategory}
              sx={{ 
                color: theme.palette.error.light,
                '&:hover': {
                  backgroundColor: alpha(theme.palette.error.main, 0.1),
                  color: theme.palette.error.main,
                }
              }}
            >
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </CategoryHeader>
      
      {category.description && (
        <Typography 
          variant="body2" 
          color="textSecondary" 
          sx={{ 
            mb: 2,
            fontStyle: 'italic',
            color: alpha(theme.palette.text.secondary, 0.8)
          }}
        >
          {category.description}
        </Typography>
      )}

      <Collapse in={expanded} timeout="auto">
        <Box sx={{ my: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
              Available Values
            </Typography>
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={() => setNewValueDialog(true)}
              sx={{
                textTransform: 'none',
                borderRadius: '6px',
                color: theme.palette.common.white,
                backgroundColor: alpha(theme.palette.primary.main, 0.8),
                '&:hover': {
                  backgroundColor: theme.palette.primary.main,
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                },
                fontWeight: 500,
                fontSize: '0.85rem',
                py: 0.75,
                px: 1.5,
              }}
            >
              Add Value
            </Button>
          </Box>

          {error && (
            <Box sx={{ 
              p: 1.5, 
              mb: 2, 
              bgcolor: alpha(theme.palette.error.main, 0.08), 
              borderRadius: 1,
              border: `1px solid ${alpha(theme.palette.error.main, 0.15)}`,
            }}>
              <Typography color="error" variant="body2">{error}</Typography>
            </Box>
          )}

          <List sx={{ 
            maxHeight: '300px', 
            overflowY: 'auto',
            pr: 1,
            '&::-webkit-scrollbar': {
              width: '4px',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: alpha(theme.palette.primary.main, 0.2),
              borderRadius: '4px',
            }
          }}>
            {values === null ? (
              <Typography variant="body2" color="textSecondary" sx={{ p: 2, textAlign: 'center' }}>
                Loading values…
              </Typography>
            ) : values.length > 0 ? (
              values.map((value) => (
                <Fade key={value.id} in={true} timeout={300}>
                  <StyledListItem>
                    <ListItemText 
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <TagIcon sx={{ mr: 1, fontSize: '0.9rem', color: alpha(theme.palette.primary.main, 0.8) }} />
                          <Typography variant="body1">
                            {formatText(getTypedValueLabel(value.value, categoryType, value.name))}
                          </Typography>
                        </Box>
                      } 
                    />
                    <ListItemSecondaryAction>
                      <Tooltip title="Delete Value">
                        <IconButton 
                          edge="end" 
                          size="small" 
                          onClick={() => handleOpenDeleteValue(value)}
                          sx={{ 
                            color: theme.palette.error.light,
                            '&:hover': {
                              backgroundColor: alpha(theme.palette.error.main, 0.1),
                              color: theme.palette.error.main,
                            }
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </ListItemSecondaryAction>
                  </StyledListItem>
                </Fade>
              ))
            ) : (
              <Box sx={{ 
                p: 3, 
                textAlign: 'center', 
                bgcolor: alpha(theme.palette.background.default, 0.4), 
                borderRadius: 2,
                border: `1px dashed ${alpha(theme.palette.divider, 0.3)}`,
              }}>
                <Typography variant="body2" color="textSecondary">
                  No values added yet. Click "Add Value" to create some.
                </Typography>
              </Box>
            )}
          </List>
        </Box>
      </Collapse>

      <Dialog 
        open={newValueDialog} 
        onClose={() => {
          setNewValueDialog(false);
          setNewValue('');
          setError('');
        }}
        PaperProps={{
          sx: {
            borderRadius: 3,
            backgroundColor: theme.palette.background.paper,
            backgroundImage: 'none',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
          }
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ 
          borderBottom: `1px solid ${theme.palette.divider}`, 
          pb: 2,
        }}>
          <Typography variant="h6" component="div" fontWeight={600}>
            Add New Value to {formatText(category.name)}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ mt: 2, pb: 3 }}>
          {renderValueInput()}
          <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 1 }}>
            Values are used as attributes for categorizing your data (e.g. "Wholesaler A", "Contract 123").
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
          <Button 
            onClick={() => {
              setNewValueDialog(false);
              setNewValue('');
              setError('');
            }}
            sx={{ 
              color: theme.palette.text.secondary,
              '&:hover': {
                backgroundColor: alpha(theme.palette.divider, 0.1),
              }
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleAddValue} 
            variant="contained"
            color="primary"
            sx={{
              borderRadius: '6px',
              backgroundColor: theme.palette.primary.main,
              textTransform: 'none',
              fontWeight: 500,
              '&:hover': {
                backgroundColor: theme.palette.primary.dark,
                boxShadow: '0 4px 10px rgba(0, 0, 0, 0.2)',
              }
            }}
          >
            Add Value
          </Button>
        </DialogActions>
      </Dialog>

      <DeleteResourceDialog
        open={Boolean(valueDeletion.target)}
        title={`Delete ${formatText(getTypedValueLabel(
          valueDeletion.target?.value,
          categoryType,
          valueDeletion.target?.name || valueDeletion.target?.id || 'Value',
        ))}`}
        preview={valueDeletion.preview}
        scopeChanged={valueDeletion.scopeChanged}
        executionError={valueDeletion.executionError}
        onConfirm={valueDeletion.confirm}
        onClose={valueDeletion.close}
        busy={valueDeletion.busy}
      />
    </StyledPaper>
  );
}

export default CategoryCard;
